/**
 * Echo Hunt -- the lucid interlude prompt (P11, §8.4).
 *
 * VISIBILITY-MATRIX ROW (§16.0): there is no row for this step in the original table, so it is stated
 * here and the plan-doc gains one. It is the "Narrated interlude" row:
 *  - knows real/fake?       NO. It receives no bullets at all -- only the world, the character and the
 *                           storyline summary. There is literally no field for a label in its input.
 *  - sees world/character?  YES. It is narration IN the story, so it has to know where the character is
 *                           and who they are, exactly as the weaver does (invariant #4's second half).
 *  - sees other bullets?    NO. There are none: the interlude happens BETWEEN rounds.
 *  - sees corpus?           NO.
 *
 * ## Why this is a separate step rather than part of the weaver
 *
 * §8.4 wants the realisation "narrated in an interlude outside the hunt". Making the weaver produce it
 * would put it in a round -- and a round's bullets are the material being judged, so an interlude would
 * have to be a bullet, which is a fake with extra steps. A separate, once-per-story call keeps the hunt's
 * labels untouched.
 *
 * ## What it must NOT do
 *
 * It must not interpret the user's dreams, and it must not explain what the game is training. §8.2's
 * dramatic irony is the model: the character notices "something off, yet familiar", checks, and the
 * realisation is ordinary reasoning finally arriving. The prose is the model of the sequence the user is
 * practising -- not a lesson about it.
 *
 * See `Docs/Plans/Others/2026-10-19_EchoHunt.md` §8.2, §8.4, §16.0, §16.2 P11.
 */
import type {JSONSchemaLike} from "../LLM.js";
import {HOUSE_STYLE_RULES} from "./IndexPrompt.js";
import {DescribeCautionVsOpportunity, DescribeCrazinessTolerance} from "./Weave.js";

export function Interlude_SystemPrompt(): string {
	return [
		"You write the closing passage of a story installment: the moment the protagonist finally notices",
		"that the situation they have been treating as ordinary is not what they think it is.",
		"Plain, concrete prose. No interpretation of the story and no explanation of the writing.",
		"Reply with JSON only. No prose outside the JSON, no code fences.",
	].join("\n");
}

export type Interlude_UserPromptOptions = {
	worldName: string;
	worldBible: string;
	worldCrazinessTolerance: number;
	characterName: string;
	characterCautionVsOpportunity: number;
	characterNotes: string;
	/** the storyline so far -- the same summary the weaver receives (§8.1) */
	summary?: string|n;
	/** how suspicious the character had become (0..1), so the passage can start as far along as it is.
	 * A number, not a label: it is derived from the player's past rounds and says nothing about the current
	 * one (§8.4). */
	suspicion: number;
	/** on a retry: the problems found in the previous reply */
	fixNotes?: string|n;
};

/** Builds the interlude prompt. Deliberately short: it is one passage, and a long list of rules is what
 * makes narration read like a checklist. */
export function Interlude_UserPrompt(opts: Interlude_UserPromptOptions): string {
	const {
		worldName, worldBible, worldCrazinessTolerance,
		characterName, characterCautionVsOpportunity, characterNotes,
		summary, suspicion, fixNotes,
	} = opts;

	const parts = [
		"## The situation",
		"Someone is having an experience that is, in fact, a dream, and has believed they were awake throughout.",
		`They are in ${worldName}, which tolerates absurdity ${DescribeCrazinessTolerance(worldCrazinessTolerance)}.`,
		worldBible.trim(),
		"",
		"## The character",
		`Name: ${characterName}`,
		`Disposition: ${DescribeCautionVsOpportunity(characterCautionVsOpportunity)}.`,
		characterNotes.trim(),
		"",
	];

	if (summary != null && summary.trim().length > 0) {
		parts.push(
			"## What they have been doing",
			summary.trim(),
			"",
		);
	}

	parts.push(
		"## How far along the realisation they are",
		`${Math.round(Math.max(0, Math.min(1, suspicion)) * 100)}% of the way to noticing.`,
		suspicion >= .8
			? "They are almost there: something has been accumulating and they are about to act on it."
			: "They have noticed fragments and dismissed them; this passage is where the fragments finally add up.",
		"",
		"## What to write",
		"One passage of 120 to 200 words, in the past tense, describing this sequence in order:",
		"1. Something specific and small feels wrong, or feels familiar in a way it should not.",
		"2. They check it -- the ordinary check a person does (a hand, a door, a word, a reflection, counting fingers, reading something twice).",
		"3. The check gives them an answer they cannot explain away, and they understand where they are.",
		"4. Briefly -- one or two sentences -- they act on it, and then the experience ends and they are awake.",
		"",
		"## Rules",
		Rule("Keep it concrete. Name the thing they checked, and say what it did."),
		Rule("No explanation of what any of this means, no moral, no summary of the story's themes."),
		Rule("Do not mention the reader, the game, or that anything has been training them."),
		Rule("Do not list what they should have noticed earlier. They notice it now, once."),
		Rule("They are not frightened and not triumphant. The tone is a person confirming an ordinary suspicion."),
		Rule("Write it as narration, not as bullets -- no headings, no list markers, no quotation marks around the whole passage."),
		"",
		"## House style",
		HOUSE_STYLE_RULES,
		"",
		"## Output",
		"Reply with ONE JSON object:",
		`{"interlude": "..."}`,
	);

	if (fixNotes != null && fixNotes.trim().length > 0) {
		parts.push(
			"",
			"## Fix these problems from your previous attempt",
			fixNotes.trim(),
			"",
			"Reply with the full passage again.",
		);
	}

	return parts.join("\n");
}

function Rule(text: string): string {
	return `- ${text}`;
}

/** The JSON shape `CallLLM` validates against. Loose on length beyond a floor: a passage that came back
 * at 90 words is still a usable interlude, and rejecting it would cost a retry to fix arithmetic. */
export function Interlude_JSONSchema(): JSONSchemaLike {
	return {
		type: "object",
		required: ["interlude"],
		properties: {
			interlude: {type: "string", minLength: 1},
		},
	};
}
